import { cn } from '@/lib/utils';
import { HiroMatchLogo } from '@/components/branding/HiroMatchLogo';

type BrandLoadingScreenProps = {
  /** When false, renders only the animated mark (for inline/embedded use). */
  fullScreen?: boolean;
  showWordmark?: boolean;
  className?: string;
};

export function HiroMarkLoader({ className = 'h-14 w-14' }: { className?: string }) {
  return (
    <div className={cn('relative', className)} aria-hidden="true">
      <div
        className="pointer-events-none absolute inset-0 -z-10 scale-[1.75] rounded-full bg-violet-400/25 blur-2xl animate-hiro-glow"
      />
      <HiroMatchLogo className="h-full w-full" ariaLabel="" />
    </div>
  );
}

export function BrandWordmark({
  animated = false,
  className,
}: {
  animated?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-0',
        animated && 'animate-hiro-breathe',
        className,
      )}
    >
      {animated ? (
        <HiroMarkLoader className="h-14 w-14" />
      ) : (
        <HiroMatchLogo className="h-14 w-14" ariaLabel="HiroMatch" />
      )}
      <span className="-mt-1 font-display text-lg font-semibold tracking-tight text-foreground/80">
        HiroMatch
      </span>
    </div>
  );
}

export default function BrandLoadingScreen({
  fullScreen = true,
  showWordmark = true,
  className,
}: BrandLoadingScreenProps) {
  const content = (
    <div
      className={cn(!fullScreen && className)}
      role="status"
      aria-live="polite"
      aria-label="Loading"
    >
      {showWordmark ? <BrandWordmark animated /> : <HiroMarkLoader className="h-14 w-14" />}
    </div>
  );

  if (!fullScreen) return content;

  return (
    <div
      className={cn(
        'fixed inset-0 flex items-center justify-center px-4',
        'min-h-dvh h-dvh w-full',
        'bg-gradient-to-br from-background via-violet-50/50 to-indigo-50/40',
        'dark:from-background dark:via-violet-950/25 dark:to-indigo-950/20',
        className,
      )}
    >
      {content}
    </div>
  );
}
